import React, { useEffect, useRef, useState } from "react";

import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Box, Button, Chip, CircularProgress, Popover, Popper, Stack, TextField, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { SelectAutocomplete } from "components/forms";
import dayjs from "dayjs";
import { capitalize } from "lodash";
import { useDateFormatSelect } from "store";

type FilterOptionObject = { id: number; name: string };
type FilterOptionDateItem = { from: string; to: string };
type FilterOptionPrimitive = number | string | boolean;
type FilterOption = FilterOptionPrimitive | FilterOptionObject | FilterOptionDateItem;

interface WorkspaceFilters {
  [key: string]: FilterOption[];
}

interface WorkspaceFiltersFromDB {
  [key: string]: FilterOption[];
}

const FILTER_LABEL_MAP: Record<string, string> = {
  document_type: "Altius Classification",
  file_type: "Type",
  file_status: "Status",
  tags: "Tags",
  last_updated: "Last Updated",
  source: "Source",
  upload_date: "Upload Date"
};

const getFilterLabel = (key: string): string => FILTER_LABEL_MAP[key] ?? capitalize(key).replace(/_/g, " ");

const isObjectOption = (val: FilterOption): val is FilterOptionObject =>
  val !== null && typeof val === "object" && "id" in val;

const getOptionLabel = (val: FilterOption): string => {
  if (isObjectOption(val)) return val.name;
  return String(val);
};

interface Props {
  handleChangeFilter: (filters: WorkspaceFilters) => void;
  filtersList: WorkspaceFilters;
  filterTypes?: Record<string, "date" | "single" | "raw" | "uppercase">;
  applyMode?: boolean;
  state?: WorkspaceFilters;
  beginningElement?: JSX.Element;
  getFiltersOptions?: () => Promise<WorkspaceFiltersFromDB>;
  defaultFilterKeys?: string[];
  skipDefaults?: boolean;
  isLoading?: boolean;
}

export const WorkspaceFilter: React.FC<Props> = ({
  handleChangeFilter,
  filtersList,
  filterTypes = {},
  state = {},
  applyMode = false,
  beginningElement,
  getFiltersOptions,
  defaultFilterKeys = [],
  skipDefaults = false,
  isLoading = false
}) => {
  const effectiveDefaultKeys = skipDefaults ? [] : defaultFilterKeys;
  const dateFormat = useDateFormatSelect();
  const [openAddPopover, setOpenAddPopover] = useState(false);
  const [filterEdit, setFilterEdit] = useState<string>("");
  const [buttonsOpen, setButtonsOpen] = useState(false);

  const [filters, setFilters] = useState<WorkspaceFilters>(() => {
    const init = { ...state };
    effectiveDefaultKeys.forEach((key) => {
      if (!(key in init)) init[key] = [];
    });
    return init;
  });
  const [selectedValues, setSelectedValues] = useState<FilterOption[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<string[]>(() =>
    Array.from(new Set([...Object.keys(state), ...effectiveDefaultKeys]))
  );
  const [pendingFilters, setPendingFilters] = useState<WorkspaceFilters>({});

  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  const anchorRef = useRef<HTMLElement>();
  const addRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const isDateFilter = (key: string) => filterTypes[key] === "date";
  const isSingleFilter = (key: string) => filterTypes[key] === "single";
  const filtersNumber = Object.keys(filters).length;

  const commitFilters = (copy: WorkspaceFilters) => {
    setFilters(copy);
    if (!applyMode) {
      handleChangeFilter(copy);
    } else {
      setPendingFilters(copy);
      setButtonsOpen(true);
    }
  };

  const handleClose = () => {
    setFilterEdit("");
    setSelectedValues([]);
    setDateFrom("");
    setDateTo("");
  };

  const handleSet = () => {
    const copy = { ...filters };
    copy[filterEdit] = selectedValues;
    commitFilters(copy);
  };

  const handleSetDate = () => {
    const copy = { ...filters };
    copy[filterEdit] = dateFrom || dateTo ? [{ from: dateFrom, to: dateTo }] : [];
    commitFilters(copy);
  };

  const handleAddFilters = () => {
    const copy = { ...filters };

    Object.keys(copy).forEach((filter) => {
      if (selectedFilters.includes(filter)) return;
      delete copy[filter];
    });
    selectedFilters.forEach((filter) => {
      copy[filter] = copy[filter] || [];
    });

    setFilters(copy);
    if (!applyMode) {
      setSelectedFilters(Object.keys(copy));
    } else {
      setPendingFilters(copy);
      setButtonsOpen(true);
    }
    setOpenAddPopover(false);
  };

  const removeFilter = (filter: string) => {
    const copy = { ...filters };
    delete copy[filter];
    setFilters(copy);
    setSelectedFilters((prev) => prev.filter((f) => f !== filter));
    if (!applyMode) {
      handleChangeFilter(copy);
    } else {
      setPendingFilters(copy);
      handleChangeFilter(copy);
      setButtonsOpen(false);
    }
  };

  const openFilter = (e: React.MouseEvent, filter: string) => {
    anchorRef.current = e.target as HTMLElement;
    getFiltersOptions?.();
    setFilterEdit(filter);
  };

  const handleClearFilters = () => {
    setFilters({});
    setSelectedFilters([]);
    if (!applyMode) {
      handleChangeFilter({});
    } else {
      setPendingFilters({});
      handleChangeFilter({});
      setButtonsOpen(false);
    }
  };

  const handleApply = () => {
    handleChangeFilter(pendingFilters);
    setButtonsOpen(false);
  };

  useEffect(() => {
    if (!filterEdit) return;
    if (isDateFilter(filterEdit)) {
      const existing = filters[filterEdit]?.[0];
      const dateItem = existing && typeof existing === "object" && "from" in existing ? existing : null;
      setDateFrom(dateItem?.from ?? "");
      setDateTo(dateItem?.to ?? "");
    } else {
      setSelectedValues(filters[filterEdit] || []);
    }
  }, [filterEdit]);

  const filterHasObjectOptions = (key: string): boolean => {
    const opts = filtersList[key];
    return Array.isArray(opts) && opts.length > 0 && isObjectOption(opts[0]);
  };

  const renderChipLabel = (filter: string, values: FilterOption[]): string => {
    if (isDateFilter(filter)) {
      const dateItem = values[0] && typeof values[0] === "object" && "from" in values[0] ? values[0] : null;
      const from = dateItem?.from ? dayjs(dateItem.from).format("MMM D, YYYY") : null;
      const to = dateItem?.to ? dayjs(dateItem.to).format("MMM D, YYYY") : null;
      const label = getFilterLabel(filter);
      if (from && to) return `${label}: ${from} – ${to}`;
      if (from) return `${label}: from ${from}`;
      if (to) return `${label}: to ${to}`;
      return label;
    }
    return `${getFilterLabel(filter)} (${values.length})`;
  };

  const renderOptionLabel = (option: string): string => getFilterLabel(option);

  const renderFilterBody = (): JSX.Element | null => {
    if (!filterEdit) return null;

    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minWidth: "300px", py: "40px" }}>
          <CircularProgress size={28} sx={{ color: "#26A6AE" }} />
        </Box>
      );
    }

    if (isDateFilter(filterEdit)) {
      return (
        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", gap: "16px" }}>
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            maxDate={dateTo ? dayjs(dateTo) : undefined}
            value={dateFrom ? dayjs(dateFrom) : null}
            label="From"
            onChange={(val) => {
              if (val && val.isValid()) setDateFrom(val.format("YYYY-MM-DD"));
              else setDateFrom("");
            }}
            slotProps={{
              inputAdornment: { position: "start" },
              popper: { sx: { ".MuiPickersCalendarHeader-labelContainer": { fontSize: "0.9rem" } } }
            }}
          />
          -
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            minDate={dateFrom ? dayjs(dateFrom) : undefined}
            value={dateTo ? dayjs(dateTo) : null}
            label="To"
            onChange={(val) => {
              if (val && val.isValid()) setDateTo(val.format("YYYY-MM-DD"));
              else setDateTo("");
            }}
            slotProps={{
              inputAdornment: { position: "start" },
              popper: { sx: { ".MuiPickersCalendarHeader-labelContainer": { fontSize: "0.9rem" } } }
            }}
          />
        </Stack>
      );
    }

    if (filterHasObjectOptions(filterEdit)) {
      return (
        <SelectAutocomplete<FilterOptionObject, true>
          multiple
          disableCloseOnSelect
          selectAll
          limitTags={1}
          openOnFocus
          options={(filtersList[filterEdit] || []) as FilterOptionObject[]}
          getOptionLabel={(opt) => opt.name}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          compareParam={(opt) => opt.name}
          renderInput={(params) => <TextField {...params} label="Filters" inputRef={inputRef} />}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          )}
          renderTags={(tagValue, getTagProps) => (
            <div style={{ maxHeight: "160px", overflowY: "auto", overflowX: "hidden" }}>
              {tagValue.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.id} label={option.name} />
              ))}
            </div>
          )}
          sx={{ maxWidth: "300px", minWidth: "300px" }}
          value={(selectedValues as FilterOptionObject[]) || []}
          setValue={(value) => setSelectedValues(value as FilterOptionObject[])}
        />
      );
    }

    if (isSingleFilter(filterEdit)) {
      return (
        <SelectAutocomplete<string>
          openOnFocus
          options={(filtersList[filterEdit] || []).map((v) => getOptionLabel(v))}
          renderInput={(params) => <TextField {...params} label="Filters" inputRef={inputRef} />}
          renderOption={(props, option) => (
            <li {...props} key={option}>
              {renderOptionLabel(option)}
            </li>
          )}
          sx={{ maxWidth: "300px", minWidth: "300px" }}
          value={(selectedValues[0] as string) ?? null}
          setValue={(value) => setSelectedValues(value ? [value] : [])}
        />
      );
    }

    const getLabel = (value: string): string => {
      if (filterEdit === "file_type") {
        return value.toUpperCase();
      }
      return value;
    };

    return (
      <SelectAutocomplete<string, true>
        multiple
        disableCloseOnSelect
        selectAll
        limitTags={1}
        openOnFocus
        options={(filtersList[filterEdit] || []).map((v) => getOptionLabel(v))}
        renderInput={(params) => <TextField {...params} label="Filters" inputRef={inputRef} />}
        renderOption={(props, option) => (
          <li {...props} key={option}>
            {getLabel(option)}
          </li>
        )}
        renderTags={(tagValue, getTagProps) => (
          <div style={{ maxHeight: "160px", overflowY: "auto", overflowX: "hidden" }}>
            {tagValue.map((option, index) => (
              <Chip {...getTagProps({ index })} key={index} label={getLabel(option)} />
            ))}
          </div>
        )}
        sx={{ maxWidth: "300px", minWidth: "300px" }}
        value={(selectedValues as FilterOptionPrimitive[]).map((v) => getOptionLabel(v)) || []}
        setValue={(value) => setSelectedValues(value as string[])}
      />
    );
  };

  return (
    <>
      <Stack sx={{ flexDirection: "row", justifyContent: "space-between", gap: "15px" }}>
        <Stack direction="row" sx={{ alignItems: "center", flexWrap: "wrap", gap: "15px" }}>
          {beginningElement}
          <Button
            ref={addRef}
            variant="outlined"
            color="secondary"
            sx={{ borderColor: "#dae0e2" }}
            onClick={() => {
              getFiltersOptions?.();
              setOpenAddPopover(true);
            }}
            startIcon={<FilterAltOutlinedIcon sx={{ fontSize: "1.2rem", marginTop: "-3px" }} />}
          >
            Filter ({filtersNumber})
          </Button>

          {Object.entries(filters).map(([filter, values]) => (
            <Chip
              key={filter}
              sx={{ height: "26px", fontSize: "15px", color: "#0a3142" }}
              label={renderChipLabel(filter, values)}
              onDelete={() => removeFilter(filter)}
              onClick={(e) => openFilter(e, filter)}
            />
          ))}

          {Boolean(filtersNumber) && (
            <Button sx={{ color: "#26A6AE" }} onClick={handleClearFilters}>
              Clear All
            </Button>
          )}

          {buttonsOpen && applyMode && (
            <Box>
              <Button variant="contained" onClick={handleApply} color="primary">
                Apply
              </Button>
            </Box>
          )}
        </Stack>
      </Stack>

      <Popover
        open={openAddPopover}
        anchorEl={addRef.current}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        onClose={() => {
          setSelectedFilters(Object.keys(filters));
          setOpenAddPopover(false);
        }}
        disableEnforceFocus
        disableAutoFocus
      >
        <Stack sx={{ alignItems: "stretch", p: "30px", gap: "30px", maxWidth: "500px" }}>
          <Stack
            sx={{ borderBottom: "1px solid #dae0e2", pb: "5px" }}
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography sx={{ fontSize: "15px" }}>Add Filter</Typography>
            <Stack flexDirection="row">
              <Button
                sx={{ color: "#26A6AE" }}
                variant="text"
                onClick={() => {
                  setSelectedFilters(Object.keys(filters));
                  setOpenAddPopover(false);
                }}
              >
                Cancel
              </Button>
              <Button
                sx={{ color: "#26A6AE", ":disabled": { backgroundColor: "#FFF", color: "#bac7ce" } }}
                variant="text"
                onClick={handleAddFilters}
              >
                Save
              </Button>
            </Stack>
          </Stack>

          {isLoading && (
            <Box
              sx={{ display: "flex", justifyContent: "center", alignItems: "center", minWidth: "300px", py: "40px" }}
            >
              <CircularProgress size={28} sx={{ color: "#26A6AE" }} />
            </Box>
          )}

          {filtersList && !isLoading && (
            <SelectAutocomplete<string, true>
              multiple
              disableCloseOnSelect
              selectAll
              openOnFocus
              limitTags={1}
              PopperComponent={(props) => (
                <Popper
                  {...props}
                  sx={{
                    "& .MuiAutocomplete-option": {
                      "&:hover": { backgroundColor: "#edf1f9 !important" }
                    }
                  }}
                />
              )}
              slotProps={{ listbox: { sx: { maxHeight: 200, overflow: "auto" } } }}
              options={Array.from(new Set([...Object.keys(filtersList), ...Object.keys(filterTypes)]))}
              renderInput={(params) => <TextField {...params} label="Filters" inputRef={inputRef} />}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  {renderOptionLabel(option)}
                </li>
              )}
              renderTags={(tagValue, getTagProps) => (
                <div style={{ maxHeight: "110px", overflowY: "auto", overflowX: "hidden" }}>
                  {tagValue.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={index} label={renderOptionLabel(option)} />
                  ))}
                </div>
              )}
              sx={{ maxWidth: "300px", minWidth: "300px" }}
              value={selectedFilters}
              setValue={(value) => setSelectedFilters(value)}
            />
          )}
        </Stack>
      </Popover>

      <Popover
        open={!!filterEdit}
        anchorEl={anchorRef.current}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        onClose={() => {
          handleClose();
          setButtonsOpen(false);
        }}
        disableEnforceFocus
        disableAutoFocus
      >
        <Stack sx={{ alignItems: "stretch", p: "30px", gap: "30px" }}>
          <Stack
            sx={{ borderBottom: "1px solid #dae0e2", pb: "5px" }}
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography sx={{ fontSize: "15px" }}>{getFilterLabel(filterEdit)}</Typography>
            <Stack flexDirection="row">
              <Button
                sx={{ color: "#26A6AE" }}
                variant="text"
                onClick={() => {
                  handleClose();
                  setButtonsOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={isLoading}
                sx={{ color: "#26A6AE", ":disabled": { backgroundColor: "#FFF", color: "#bac7ce" } }}
                variant="text"
                onClick={() => {
                  isDateFilter(filterEdit) ? handleSetDate() : handleSet();
                  handleClose();
                }}
              >
                Save
              </Button>
            </Stack>
          </Stack>
          {renderFilterBody()}
        </Stack>
      </Popover>
    </>
  );
};
